'use client';

import { useOverlayTriggerState } from '@react-stately/overlays';
import { AnimatePresence, motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { Alert, AvatarScratch, Mug, Search } from '@zoomit/icons';
import { Flex, IconButton, Link, cn } from '@zoomit/primitives';
import type { MenuItemType } from '@zoomit/web-kit';
import {
  DesktopNotification,
  DesktopUserMenu,
  GlobalLoading,
  IconRenderer,
  LogoutModal,
  NotifBadge,
  OriginURL,
  onMenuClick,
  onNotificationClick,
  useLogout,
  useMenu,
} from '@zoomit/web-kit';
import { Anchor, cssVar, theme } from '@zoomit/zoomit-ds';

import { canDisplayMenuItem } from '../../../helpers/menu/canDisplayMenuItem';
import { getNestedIndicatorButtons } from '../../../helpers/menu/getNestedIndecatorButtons';
import { useWebKitCustomization } from '../../../hooks/useWebKitCustomization';
import type { MenuParserData } from '../../../types/menus';
import { HeightMotion } from '../../shared/HeightMotion';
import { DefaultMenuMoreItems } from './DefaultMenuMoreItems';
import { DefaultMenuProfile } from './DefaultMenuProfile';
import { DefaultMenuSearchSection } from './DefaultMenuSearchSection';
import DefaultMenuSubItemColumnDesktop from './DefaultMenuSubItemColumnDesktop';

const ThemeSwitch = dynamic(() => import('./ThemeSwitch'), { ssr: false });

const DefaultMenuDesktop = (props: MenuParserData) => {
  const { variant, menuData } = props;
  const menuOptions = useMenu();
  const isTransparent = menuOptions.isTransparent && variant === 'transparent';
  const basePath = useWebKitCustomization((settings) => settings.basePath);
  const tenantLogoRenderer = useWebKitCustomization((settings) => settings.tenantLogoRenderer);

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [triggeredMenuItem, setTriggeredMenuItem] = useState<{
    offset: number;
    columnCount: number;
    children: MenuItemType[];
  }>();

  const { isAuthenticated, signinRedirect, activeNavigator, isLoading } = useAuth();
  const { isLoading: signOutLoading, mutate: handleSignOut } = useLogout();

  const notificationDrawer = useOverlayTriggerState({});

  const hasTransparentStyle = isTransparent && !isSearchActive && !notificationDrawer.isOpen;

  const withTransparentTheming = (themeKey: string) => {
    if (isSearchActive || notificationDrawer.isOpen) {
      return cssVar(themeKey);
    }

    if (hasTransparentStyle) {
      return '#E8E2D8';
    }

    return cssVar(themeKey);
  };

  const handleMenuItemHover = (menuItem: MenuItemType, index: number) => {
    if (menuItem.children) {
      setTriggeredMenuItem({
        offset: index,
        children: menuItem.children,
        columnCount: menuItem.columnNumber,
      });
    } else if (triggeredMenuItem) {
      setTriggeredMenuItem(undefined);
    }
  };

  const clearTriggeredMenuItem = () => {
    if (triggeredMenuItem) {
      setTriggeredMenuItem(undefined);
    }
  };

  return (
    <header
      className={
        'before:bg-primary1 relative flex h-16 items-center px-6 py-0 transition-all duration-200 before:absolute before:top-1/2 before:right-0 before:h-7 before:w-1 before:-translate-y-1/2 before:rounded-sm'
      }
      style={{
        backgroundColor: hasTransparentStyle ? 'transparent' : cssVar(theme.menu.headerBackground),
        borderBottom: hasTransparentStyle ? 'none' : `1px solid ${cssVar(theme.borderPrimary)}`,
        backgroundImage: hasTransparentStyle
          ? 'linear-gradient(to bottom, rgba(25, 25, 35, 0.5), rgba(25, 25, 35, 0))'
          : 'none',
      }}
      data-testid="menu-desktop"
    >
      {activeNavigator === 'signinRedirect' && (
        <GlobalLoading
          Logo={() => tenantLogoRenderer({ fill: withTransparentTheming(theme.menu.zoomitTypography) })}
        />
      )}
      <Link href={`${OriginURL}/${basePath}`} style={{ display: 'flex', textDecoration: 'none' }}>
        {tenantLogoRenderer({ fill: withTransparentTheming(theme.menu.zoomitTypography) })}
      </Link>
      <nav className={'mr-[6vw] flex items-center'}>
        <ul
          className={'relative flex list-none items-center gap-8 p-0'}
          onPointerLeave={clearTriggeredMenuItem}
        >
          <div
            className={
              'after:bg-grey6 relative block after:absolute after:top-1/2 after:-left-4 after:h-[14px] after:w-px after:-translate-1/2 after:content-[""]'
            }
          >
            <DefaultMenuProfile
              icon={<Mug fill={withTransparentTheming(theme.text)} size="md" />}
              clearTriggeredMenuItem={clearTriggeredMenuItem}
              hasTransparentStyle={hasTransparentStyle}
            />
          </div>
          {menuData.items.map((item, idx, items) => {
            const indicatorButtons = getNestedIndicatorButtons(item);

            return (
              <div
                className={cn(
                  'xxl:flex relative flex h-full cursor-pointer flex-nowrap items-center justify-start text-nowrap',
                  idx > items.length - 5 && 'lg:hidden xl:flex',
                  idx > items.length - 3 && 'xl:hidden',
                )}
                key={`${item.title}-${idx}`}
              >
                <Flex
                  alignItems="center"
                  gap={3.2}
                  className={cn(
                    'relative hover:[&_div]:visible hover:[&_div]:opacity-100',
                    !hasTransparentStyle &&
                      'after:bg-grey6 after:absolute after:top-1/2 after:-right-4 after:h-px after:w-px after:-translate-y-1/2 after:content-[""]',
                  )}
                >
                  {item.url ? (
                    <a
                      onClick={() => {
                        onMenuClick({ menu: item.title, device: 'desktop' });
                      }}
                      href={item.url}
                      style={{ pointerEvents: 'visible' }}
                      key={`${item.title}-${idx}`}
                      rel={item.noFollow ? 'nofollow' : undefined}
                    >
                      <div
                        className={cn(
                          'text-body hover:text-primary2 hover:[&_svg]:fill-primary2 flex cursor-pointer items-center gap-1.5 leading-7 font-black',
                          hasTransparentStyle ? 'text-grey1' : 'text-text',
                        )}
                        onPointerEnter={() => handleMenuItemHover(item, idx)}
                      >
                        {canDisplayMenuItem.renderIcon(item.displayType) && item.icon?.content && (
                          <IconRenderer icon={item.icon.content} size={'md'} />
                        )}
                        {item.title}
                      </div>
                    </a>
                  ) : (
                    <div
                      className={cn(
                        'text-body hover:text-primary2 hover:[&_svg]:fill-primary2 flex cursor-pointer items-center gap-1.5 leading-7 font-black',
                        hasTransparentStyle ? 'text-grey1' : 'text-text',
                      )}
                      onPointerEnter={() => handleMenuItemHover(item, idx)}
                    >
                      {canDisplayMenuItem.renderIcon(item.displayType) && item.icon?.content && (
                        <IconRenderer icon={item.icon.content} size={'md'} />
                      )}
                      {item.title}
                    </div>
                  )}
                  <AnimatePresence>
                    {triggeredMenuItem !== undefined &&
                      triggeredMenuItem.children?.length > 0 &&
                      triggeredMenuItem.offset === idx && (
                        <div className="shadow-main absolute top-6 right-0 z-10 origin-right bg-transparent px-px pt-3 pb-px transition-all duration-200">
                          <AnimatePresence>
                            <motion.div
                              key={triggeredMenuItem.offset}
                              initial={{ opacity: 0 }}
                              exit={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.1 }}
                              className="bg-card shadow-elevation h-auto w-full rounded-md"
                            >
                              <HeightMotion>
                                <DefaultMenuSubItemColumnDesktop
                                  key={triggeredMenuItem.offset}
                                  items={triggeredMenuItem.children}
                                />
                              </HeightMotion>
                              {indicatorButtons.length > 0 && (
                                <Flex
                                  alignItems={'center'}
                                  flexWrap={'nowrap'}
                                  spacing={{ py: 'x4', mx: 'x4' }}
                                  style={{ borderTop: '1px solid', borderColor: cssVar(theme.grey6) }}
                                >
                                  {indicatorButtons.map((button, buttonIdx) => {
                                    if (!button) return null;
                                    return (
                                      <Anchor
                                        key={buttonIdx}
                                        style={{ fontSize: 14 }}
                                        href={button.url || ''}
                                        rel={button.nofollow || !button.url ? 'nofollow' : undefined}
                                        variant={'text'}
                                        rightIcon={button.icon && <IconRenderer icon={button.icon.content} />}
                                      >
                                        {button.title}
                                      </Anchor>
                                    );
                                  })}
                                </Flex>
                              )}
                            </motion.div>
                          </AnimatePresence>
                        </div>
                      )}
                  </AnimatePresence>
                </Flex>
              </div>
            );
          })}

          <DefaultMenuMoreItems
            isTransparent={isTransparent}
            items={menuData.items}
            onHover={clearTriggeredMenuItem}
          />

          <Flex
            onMouseOver={clearTriggeredMenuItem}
            onClick={() => setIsSearchActive(!isSearchActive)}
            className={cn(
              'text-body cursor-pointer items-center gap-1 leading-7 font-black',
              isSearchActive ? 'pointer-events-none' : 'pointer-events-auto',
              hasTransparentStyle ? 'text-grey1' : 'text-text',
            )}
          >
            <Search fill={hasTransparentStyle ? '#383E43' : cssVar(theme.background2)} size="md" />
            جستجو
          </Flex>
        </ul>
      </nav>

      <DefaultMenuSearchSection
        isSearchOpen={isSearchActive}
        setIsSearchOpen={setIsSearchActive}
        searchInstanceRenderer={props.searchInstanceRenderer}
      />

      <Flex className={'absolute left-0 h-full justify-start gap-6 px-6'} alignItems="center">
        <div className={'flex items-center justify-center'}>
          <ThemeSwitch hasTransparentStyle={hasTransparentStyle} />
        </div>
        {isAuthenticated && (
          <div className={'flex items-center justify-center'}>
            <IconButton
              onClick={() => {
                notificationDrawer.toggle();
                onNotificationClick();
              }}
              style={{ position: 'relative' }}
            >
              <NotifBadge />
              {notificationDrawer.isOpen && (
                <div
                  className={
                    'bg-primary1 absolute top-[38px] left-1/2 z-10 h-[3px] w-[22px] -translate-x-1/2 rounded-sm'
                  }
                />
              )}
              <Alert
                className={'cursor-pointer'}
                size="md"
                fill={hasTransparentStyle ? cssVar(theme.white) : cssVar(theme.text)}
              />
            </IconButton>
          </div>
        )}
        {isAuthenticated ? (
          <DesktopUserMenu isTransparent={hasTransparentStyle} setShowLogoutModal={setShowLogoutModal} />
        ) : (
          <IconButton onClick={() => signinRedirect({ url_state: window.location.href })}>
            <AvatarScratch size="lg" fill={hasTransparentStyle ? cssVar(theme.white) : cssVar(theme.text)} />
          </IconButton>
        )}
        {(isAuthenticated || isLoading) && showLogoutModal && (
          <LogoutModal
            mutate={handleSignOut}
            isLoading={signOutLoading}
            onClose={() => setShowLogoutModal(false)}
          />
        )}
      </Flex>

      {isAuthenticated && notificationDrawer.isOpen && (
        <AnimatePresence>
          <DesktopNotification menuHeight={64} onClose={notificationDrawer.close} />
        </AnimatePresence>
      )}
    </header>
  );
};

export default DefaultMenuDesktop;
